import { useIntl } from "react-intl";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import EmailBlockTypeEnum from "../../../../models/EmailBlockTypeEnum";
import IEmailBlock, { IEmailColumnsBlock, IEmailLeafBlock } from "../../../../models/IEmailBlock";
import IEmailDocument from "../../../../models/IEmailDocument";
import {
    blockIconClass,
    blockLabelKey,
    blockSummary,
    findBlock,
    moveInColumn,
    moveTopLevel,
} from "../../../../services/EmailDocumentUtils";

type Props = {
    document: IEmailDocument;
    selectedBlockId: string | null;
    /** Leaf types offered by the per-column "add" select. */
    leafBlockTypes: EmailBlockTypeEnum[];
    onChange: (next: IEmailDocument) => void;
    onSelect: (id: string | null) => void;
    onDuplicate: (id: string) => void;
    onRemove: (id: string) => void;
    onAddToColumn: (columnsId: string, columnIndex: 0 | 1, type: EmailBlockTypeEnum) => void;
    disabled?: boolean;
};

type RowProps = {
    block: IEmailBlock;
    selected: boolean;
    onSelect: (id: string) => void;
    onDuplicate: (id: string) => void;
    onRemove: (id: string) => void;
    disabled?: boolean;
};

const BlockRow = ({ block, selected, onSelect, onDuplicate, onRemove, disabled }: RowProps) => {
    const intl = useIntl();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id, disabled });
    const style = { transform: CSS.Transform.toString(transform), transition };
    const summary = blockSummary(block, (id) => intl.formatMessage({ id }));

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`block-row ${selected ? "selected" : ""} ${isDragging ? "dragging" : ""}`}
            onClick={() => onSelect(block.id)}
            data-testid={`block-row-${block.id}`}>
            <div className={"row-drag-handle"} {...attributes} {...listeners} aria-label={intl.formatMessage({ id: "drag_to_reorder" })}>
                <i className={"fas fa-grip-vertical"} aria-hidden={true} />
            </div>
            <div className={"row-icon"}>
                <i className={blockIconClass(block.type)} aria-hidden={true} />
            </div>
            <div className={"row-body"}>
                <div className={"row-type"}>{intl.formatMessage({ id: blockLabelKey(block.type) })}</div>
                {summary ? <div className={"row-summary"}>{summary}</div> : null}
            </div>
            <div className={"row-actions"}>
                <button
                    type={"button"}
                    className={"btn btn-sm btn-outline-secondary"}
                    title={intl.formatMessage({ id: "duplicate" })}
                    aria-label={intl.formatMessage({ id: "duplicate" })}
                    disabled={disabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate(block.id);
                    }}>
                    <i className={"far fa-clone"} aria-hidden={true} />
                </button>
                <button
                    type={"button"}
                    className={"btn btn-sm btn-outline-danger"}
                    title={intl.formatMessage({ id: "delete" })}
                    aria-label={intl.formatMessage({ id: "delete" })}
                    disabled={disabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(block.id);
                    }}>
                    <i className={"far fa-trash-alt"} aria-hidden={true} />
                </button>
            </div>
        </div>
    );
};

type ColumnsProps = Omit<RowProps, "block"> & {
    block: IEmailColumnsBlock;
    selectedBlockId: string | null;
    leafBlockTypes: EmailBlockTypeEnum[];
    onAddToColumn: (columnsId: string, columnIndex: 0 | 1, type: EmailBlockTypeEnum) => void;
};

const ColumnsBlockView = (props: ColumnsProps) => {
    const intl = useIntl();
    const { block } = props;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: block.id,
        disabled: props.disabled,
    });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`columns-block ${props.selected ? "selected" : ""}`}
            data-testid={`block-row-${block.id}`}>
            <div className={"columns-header"} onClick={() => props.onSelect(block.id)}>
                <div className={"row-drag-handle"} {...attributes} {...listeners}>
                    <i className={"fas fa-grip-vertical"} aria-hidden={true} />
                </div>
                <i className={blockIconClass(block.type)} aria-hidden={true} />
                <strong>{intl.formatMessage({ id: blockLabelKey(block.type) })}</strong>
                <span className={"text-muted small"}>{(block.widths || [50, 50]).join("% / ")}%</span>
                <span className={"ms-auto row-actions"}>
                    <button
                        type={"button"}
                        className={"btn btn-sm btn-outline-secondary"}
                        title={intl.formatMessage({ id: "duplicate" })}
                        disabled={props.disabled}
                        onClick={(e) => {
                            e.stopPropagation();
                            props.onDuplicate(block.id);
                        }}>
                        <i className={"far fa-clone"} aria-hidden={true} />
                    </button>
                    <button
                        type={"button"}
                        className={"btn btn-sm btn-outline-danger"}
                        title={intl.formatMessage({ id: "delete" })}
                        disabled={props.disabled}
                        onClick={(e) => {
                            e.stopPropagation();
                            props.onRemove(block.id);
                        }}>
                        <i className={"far fa-trash-alt"} aria-hidden={true} />
                    </button>
                </span>
            </div>
            <div className={"columns-grid"}>
                {[0, 1].map((c) => {
                    const col = block.columns[c];
                    const ids = col.blocks.map((b) => b.id);

                    return (
                        <div key={c} className={"column-cell"}>
                            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                                {col.blocks.map((leaf: IEmailLeafBlock) => (
                                    <BlockRow
                                        key={leaf.id}
                                        block={leaf}
                                        selected={props.selectedBlockId === leaf.id}
                                        onSelect={props.onSelect}
                                        onDuplicate={props.onDuplicate}
                                        onRemove={props.onRemove}
                                        disabled={props.disabled}
                                    />
                                ))}
                            </SortableContext>
                            <select
                                className={"form-select form-select-sm column-add"}
                                value={""}
                                disabled={props.disabled}
                                aria-label={intl.formatMessage({ id: "add_block" })}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        props.onAddToColumn(block.id, c as 0 | 1, e.target.value as EmailBlockTypeEnum);
                                    }
                                }}>
                                <option value={""}>{`+ ${intl.formatMessage({ id: "add_block" })}`}</option>
                                {props.leafBlockTypes.map((type) => (
                                    <option key={type} value={type}>
                                        {intl.formatMessage({ id: blockLabelKey(type) })}
                                    </option>
                                ))}
                            </select>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/**
 * Vertical, drag-to-reorder list of the document's blocks. Top-level blocks reorder among
 * themselves; blocks inside a column reorder within that column. One DndContext covers both
 * so nested contexts never fight over pointer events.
 */
const BlockCanvas = (props: Props) => {
    const intl = useIntl();
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const topIds = props.document.blocks.map((b) => b.id);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const activeId = String(active.id);
        const overId = String(over.id);
        const a = findBlock(props.document, activeId);
        const o = findBlock(props.document, overId);

        if (!a || !o) {
            return;
        }

        if (!a.parentId && !o.parentId) {
            props.onChange(moveTopLevel(props.document, activeId, overId));
        } else if (a.parentId && a.parentId === o.parentId && a.columnIndex === o.columnIndex) {
            props.onChange(moveInColumn(props.document, a.parentId, a.columnIndex!, activeId, overId));
        }
    };

    return (
        <div className={"BlockCanvas"}>
            {props.document.blocks.length === 0 ? (
                <div className={"canvas-empty"}>{intl.formatMessage({ id: "email_template_empty_hint" })}</div>
            ) : null}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={topIds} strategy={verticalListSortingStrategy}>
                    {props.document.blocks.map((block) =>
                        block.type === EmailBlockTypeEnum.COLUMNS ? (
                            <ColumnsBlockView
                                key={block.id}
                                block={block}
                                selected={props.selectedBlockId === block.id}
                                selectedBlockId={props.selectedBlockId}
                                leafBlockTypes={props.leafBlockTypes}
                                onSelect={props.onSelect}
                                onDuplicate={props.onDuplicate}
                                onRemove={props.onRemove}
                                onAddToColumn={props.onAddToColumn}
                                disabled={props.disabled}
                            />
                        ) : (
                            <BlockRow
                                key={block.id}
                                block={block}
                                selected={props.selectedBlockId === block.id}
                                onSelect={props.onSelect}
                                onDuplicate={props.onDuplicate}
                                onRemove={props.onRemove}
                                disabled={props.disabled}
                            />
                        )
                    )}
                </SortableContext>
            </DndContext>
        </div>
    );
};

export default BlockCanvas;
